import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaBars,
  FaTimes,
  FaCompactDisc,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";

const MusicPage = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [currentGenre, setCurrentGenre] = useState(null);
  const [tapeInserted, setTapeInserted] = useState(false);
  const [tapePosition, setTapePosition] = useState("ejected");

  const musicGenres = [
    {
      id: 1,
      title: "Rock",
      tapeColor: "bg-amber-400",
      spotifyPlaylistId: "5FC99yXcPCU9FNNsgSmjnH",
    },
    {
      id: 2,
      title: "Classical",
      tapeColor: "bg-red-400",
      spotifyPlaylistId: "5MfuTHXZrpnPGNyTrseFwJ",
    },
    {
      id: 3,
      title: "J-Pop",
      tapeColor: "bg-blue-400",
      spotifyPlaylistId: "4YukyVN1xCnPCC13adktrw",
    },
    {
      id: 4,
      title: "India",
      tapeColor: "bg-green-400",
      spotifyPlaylistId: "30dRHsbXH56trQooXvhySU",
    },
    {
      id: 5,
      title: "Old Music",
      tapeColor: "bg-gray-400",
      spotifyPlaylistId: "7zVHIi3WONgY8KCcTXtE0O",
    },
  ];

  const insertTape = (genre) => {
    setCurrentGenre(genre);
    setTapePosition("inserting");

    setTimeout(() => {
      setTapeInserted(true);
      setTapePosition("loaded");
    }, 700);
  };

  const ejectTape = () => {
    setTapePosition("ejecting");
    setTimeout(() => {
      setTapeInserted(false);
      setCurrentGenre(null);
      setTapePosition("ejected");
    }, 700);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white">
      {/* HEADER */}
      <header className="bg-gradient-to-r from-yellow-500 to-orange-900 text-white pixel-border-bottom">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <FaCompactDisc className="text-2xl mr-2" />
              <h1 className="text-2xl font-bold pixel-font">
                MUSIC PLAYLIST
              </h1>
            </div>

            <div className="hidden md:block">
              <button
                onClick={() => navigate("/")}
                className="pixel-button bg-yellow-700 px-4 py-2"
              >
                <FaHome className="inline mr-1" /> Beranda
              </button>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="pixel-button bg-yellow-700 p-2"
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-4 bg-yellow-700 pixel-border p-3"
              >
                <button
                  onClick={() => navigate("/")}
                  className="pixel-button w-full bg-yellow-600"
                >
                  <FaHome className="inline mr-2" />
                  Kembali ke Beranda
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* MAIN */}
      <main className="container mx-auto px-4 py-10">
        {/* RADIO BODY */}
        <div className="max-w-2xl mx-auto mb-16">
          <div className="bg-gradient-to-b from-gray-700 to-gray-900 p-8 rounded-2xl pixel-border">
            {/* DISPLAY */}
            <div className="bg-black rounded-lg p-4 mb-6 text-center font-mono">
              <div className="text-green-400 text-sm">
                {tapeInserted ? "LOADED" : "INSERT TAPE"}
              </div>
              <div className="text-yellow-400 text-xs">
                {currentGenre?.title || "NO TAPE"}
              </div>
              <div className="text-gray-500 text-xs mt-1">
                {tapePosition.toUpperCase()}
              </div>
            </div>

            {tapeInserted && currentGenre && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-black rounded-lg overflow-hidden border-2 border-gray-600 mb-6"
              >
                <iframe
                  src={`https://open.spotify.com/embed/playlist/${currentGenre.spotifyPlaylistId}?theme=0`}
                  width="100%"
                  height="380"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              </motion.div>
            )}

            <div className="flex justify-center">
              <button
                onClick={ejectTape}
                disabled={!tapeInserted}
                className={`px-6 py-2 font-bold ${
                  tapeInserted
                    ? "bg-red-600 hover:bg-red-500"
                    : "bg-gray-700 cursor-not-allowed"
                }`}
              >
                EJECT TAPE
              </button>
            </div>
          </div>
        </div>

        {/* TAPE COLLECTION */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold pixel-font mb-2">
            KOLEKSI MUSIK
          </h2>
          <p className="text-gray-400">
            Klik untuk memuat playlist musik saya
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {musicGenres.map((genre) => (
            <motion.div
              key={genre.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => insertTape(genre)}
              className={`cursor-pointer ${
                currentGenre?.id === genre.id
                  ? "ring-4 ring-yellow-400"
                  : ""
              }`}
            >
              <div
                className={`${genre.tapeColor} rounded-lg p-4 pixel-border h-32 flex flex-col justify-between`}
              >
                <div className="text-center font-bold text-white pixel-font">
                  {genre.title}
                </div>

                <div className="flex justify-between">
                  <div className="w-5 h-5 bg-gray-800 rounded-full" />
                  <div className="w-5 h-5 bg-gray-800 rounded-full" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* QUOTE */}
        {tapeInserted && (
          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <FaCompactDisc className="text-6xl text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 italic">
              “Without music, life would be a mistake.”
            </p>
            <p className="text-gray-500 text-sm mt-2">
              — Friedrich Nietzsche
            </p>
          </motion.div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default MusicPage;
